import { useMemo } from 'react';

/**
 * Custom hook to normalize userAnswer for SingleCorrectAnswer
 * Handles conversion from text to index, priority: localStorage > props
 */
export function useNormalizedUserAnswer(userAnswer, userAnswerFromStorage, options) {
  return useMemo(() => {
    // Priority 1: userAnswerFromStorage (number/index from localStorage)
    if (userAnswerFromStorage !== null && userAnswerFromStorage !== undefined) {
      const numAnswer = typeof userAnswerFromStorage === 'number' 
        ? userAnswerFromStorage 
        : Number(userAnswerFromStorage);
      if (!isNaN(numAnswer)) {
        return numAnswer;
      }
    }

    // Priority 2: userAnswer from props
    if (userAnswer !== undefined && userAnswer !== null) {
      // If it's a number, use directly
      if (typeof userAnswer === 'number') {
        return userAnswer;
      }

      // If it's an array, check first element
      if (Array.isArray(userAnswer) && userAnswer.length > 0) {
        const firstAnswer = userAnswer[0];
        
        // If first element is a number, use it
        if (typeof firstAnswer === 'number') {
          return firstAnswer;
        }

        // If first element is a string, try to match with choices text to get index
        if (typeof firstAnswer === 'string') {
          const matchedOption = options.find(opt => {
            const optText = opt.text || '';
            return optText.trim().toLowerCase() === firstAnswer.trim().toLowerCase() ||
                   optText === firstAnswer;
          });
          
          if (matchedOption) {
            return matchedOption.id;
          }
        }
      }

      // If it's a string, try to match with choices text
      if (typeof userAnswer === 'string') {
        const matchedOption = options.find(opt => {
          const optText = opt.text || '';
          return optText.trim().toLowerCase() === userAnswer.trim().toLowerCase() ||
                 optText === userAnswer;
        });
        
        if (matchedOption) {
          return matchedOption.id;
        }
      }
    }

    // Fallback: userAnswerFromStorage as string
    if (userAnswerFromStorage !== null && userAnswerFromStorage !== undefined) {
      if (typeof userAnswerFromStorage === 'string') {
        const matchedOption = options.find(opt => {
          const optText = opt.text || '';
          return optText.trim().toLowerCase() === userAnswerFromStorage.trim().toLowerCase() ||
                 optText === userAnswerFromStorage;
        });
        
        if (matchedOption) {
          return matchedOption.id;
        }
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ Could not normalize userAnswer:', {
        userAnswer,
        userAnswerFromStorage,
        options: options.map(opt => ({ id: opt.id, text: opt.text })),
      });
    }

    return null;
  }, [userAnswer, userAnswerFromStorage, options]);
}

